import React, { useEffect, useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { fetchTenantUsers, TenantUserInfo, updateAdmin } from '../../redux/actions/dashboard'
import { setUser } from '../../shared/redux/actions/common'
import DashboardSection, { BUTTON_WIDTH, FIELD_GAP } from './DashboardSection'

const useStyles = makeStyles((theme) => ({
    contactRow: {
        display: 'grid',
        gridTemplateColumns: `1fr ${BUTTON_WIDTH}px`,
        alignItems: 'start',
        gap: FIELD_GAP,
        width: '100%',
    },
    contactField: {
        minWidth: 0,
    },
    button: {
        width: BUTTON_WIDTH,
        marginTop: 6,
    },
    admins: {
        marginTop: theme.spacing(1),
    },
    table: {
        '& td, & th': {
            paddingLeft: 0,
        },
    },
    you: {
        marginLeft: theme.spacing(1),
    },
    muted: {
        color: theme.palette.text.secondary,
    },
}))

interface Props {
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

export default function TenantInfo({ onSuccess, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const user = useSelector((state: any) => state.login.value.user)

    const [contact, setContact] = useState<string>(user?.contact ?? '')
    const [savingContact, setSavingContact] = useState(false)
    const [admins, setAdmins] = useState<TenantUserInfo[]>()
    const [adminsError, setAdminsError] = useState<string>()

    useEffect(() => {
        fetchTenantUsers()
            .then((result) => setAdmins(result ?? []))
            .catch((e: any) => setAdminsError(e?.message || 'unknown error'))
    }, [])

    const contactChanged = contact !== (user?.contact ?? '')

    const handleSaveContact = async () => {
        setSavingContact(true)
        try {
            const updated = await updateAdmin(user.id, { ...user, contact })
            // backend vracia TenantModel bez hesla - zachovame zvysok prihlaseneho usera
            dispatch(setUser({ ...user, ...updated, contact }))
            onSuccess(t('dashboard.contactSaved'))
        } catch {
            onError(t('dashboard.errorUpdatingAbout'))
        } finally {
            setSavingContact(false)
        }
    }

    const isMe = (admin: TenantUserInfo) => Boolean(user?.id && admin.id.toLowerCase() === String(user.id).toLowerCase())

    return (
        <DashboardSection title={t('dashboard.tenantSection')}>
            <div className={classes.contactRow}>
                <TextField
                    className={classes.contactField}
                    variant="filled"
                    size="small"
                    label={t('dashboard.contact')}
                    inputProps={{ maxLength: 1000 }}
                    value={contact}
                    FormHelperTextProps={{ style: { marginLeft: 0, marginRight: 0 } }}
                    helperText=" "
                    onChange={(e) => setContact(e.target.value)}
                />
                <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    disabled={!contactChanged || savingContact}
                    onClick={handleSaveContact}
                >
                    {t('dashboard.save')}
                </Button>
            </div>

            <div className={classes.admins}>
                <Table size="small" className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('dashboard.adminsTitle')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adminsError ? (
                            <TableRow>
                                <TableCell className={classes.muted}>
                                    {t('dashboard.errorLoadingTenantUsers')} ({adminsError})
                                </TableCell>
                            </TableRow>
                        ) : !admins ? (
                            <TableRow>
                                <TableCell>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            admins.map((admin) => (
                                <TableRow key={admin.id}>
                                    <TableCell>
                                        <Typography variant="body2" component="span">
                                            {admin.login}
                                        </Typography>
                                        {isMe(admin) ? (
                                            <Chip size="small" label={t('dashboard.you')} className={classes.you} />
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </DashboardSection>
    )
}
